// Three versions of the interface, one preference: dark (the default --
// cellars are dark), light, and the accessible version (high contrast,
// larger type and targets, no motion; src/ui/theme.ts). The choice is
// kept in localStorage and applied as data-theme on <html>; a device that
// asks for more contrast and has no stored choice starts in the accessible
// version. index.html stamps the same attribute pre-paint so there is no
// flash of the wrong palette.
import { useSyncExternalStore } from "react";

export const THEMES = ["dark", "light", "a11y"] as const;
export type Theme = (typeof THEMES)[number];

const STORAGE_KEY = "proef.theme";
const listeners = new Set<() => void>();

export function isTheme(value: string | null): value is Theme {
  return value !== null && (THEMES as readonly string[]).includes(value);
}

function prefers(query: string): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(query).matches
    : false;
}

function read(): Theme {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isTheme(stored)) return stored;
  } catch {
    // Storage blocked (private mode): fall through to the device hints.
  }
  return prefers("(prefers-contrast: more)") ? "a11y" : "dark";
}

let current: Theme = typeof window === "undefined" ? "dark" : read();

/** The browser chrome colour for each version (index.html's theme-color). */
export function themeColour(theme: Theme): string {
  if (theme === "light") return "#fdfbf7";
  if (theme === "a11y") return prefers("(prefers-color-scheme: dark)") ? "#000000" : "#ffffff";
  return "#1b1015";
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset["theme"] = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", themeColour(theme));
}

export function setTheme(theme: Theme): void {
  current = theme;
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Not persisting is fine.
  }
  applyTheme(theme);
  listeners.forEach((l) => l());
}

export function getTheme(): Theme {
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, getTheme, () => "dark");
}
